import { forwardRef, useMemo } from 'react';
import type { SyntheticEvent } from 'react';
import { Field, ErrorMessage, FieldProps } from 'formik';
import { Flex } from 'antd';
import clsx from 'clsx';
import type { InputRef } from 'antd';
import { MaskedInput, IMask } from 'antd-mask-input';
import TextError from 'components/TextError';
import FieldLabel from 'components/FieldLabel';
import If from 'components/If';
import { MaskedInputProps } from 'utils/globalTypes';
import styles from './index.module.scss';

type MaskChangeEventType = SyntheticEvent & {
  maskedValue: string;
  unmaskedValue: string;
};

/** `IMask.createPipe` is typed as returning `any`; this is the shape it actually returns. */
// eslint-disable-next-line no-unused-vars
type MaskPipe = (value: string) => string;

const InputMask = forwardRef<InputRef, MaskedInputProps>((props, ref) => {
  const {
    label,
    name,
    showErrMsg = true,
    info,
    maskOptions = {},
    mask,
    definitions,
    qaSelector,
    className,
    ...rest
  } = props;
  const maskOptionsObj = {
    lazy: true,
    ...maskOptions,
  };
  const inputClassName = clsx(styles.maskedInput, className);

  /*
   * `MaskedInput` renders its own `useState`, seeded from `value` once at mount. Its `value`-sync
   * effect masks the DOM node imperatively but never updates that state, so the next render —
   * a validation pass, a sibling field edit, a `touched` change — re-applies the raw controlled
   * value and the formatting vanishes until a focus or blur event finally sets the state. That is
   * why a pre-filled field used to show `2125551234` until it was clicked into.
   *
   * Handing it an already-masked `value` removes the mismatch: the field is formatted on first
   * paint and stays formatted across re-renders, with no focus/blur nudge. Formik still holds the
   * unmasked digits — see `onChange` below, which the validators and payload builders rely on.
   *
   * The pipe mirrors the library's own option assembly and is memoized on `mask` for the same
   * reason its `maskOptions` is, so the two can never disagree about how a value formats.
   */
  const toMaskedValue = useMemo(() => {
    const pipeOptions = {
      mask,
      definitions: {
        '0': /[0-9]/,
        ...maskOptions.definitions,
        ...definitions,
      },
      /* The library also seeds `lazy: false` here, but `maskOptionsObj` always carries a `lazy`
         and overwrites it, so restating it would be dead either way. */
      ...maskOptionsObj,
    } as Parameters<typeof IMask.createPipe>[0];

    return IMask.createPipe(pipeOptions) as MaskPipe;
  }, [mask]);

  return (
    <Flex vertical align='flex-start' className='formFieldContainer'>
      <FieldLabel label={label} info={info} />
      <Field name={name}>
        {({ field, meta, form }: FieldProps) => {
          const { setFieldValue } = form;
          const fieldStatus = meta.touched && meta.error ? 'error' : '';
          const rawValue = typeof field.value === 'string' ? field.value : '';
          /* Piping a value with no digits in it emits a bare mask literal (`(`), so leave those
             alone: an empty field has to stay empty and show its placeholder. */
          const displayValue = /\d/.test(rawValue)
            ? toMaskedValue(rawValue)
            : rawValue;
          const onChange = (e: MaskChangeEventType) => {
            setFieldValue(name, e.unmaskedValue);
          };
          return (
            <MaskedInput
              ref={ref}
              id={name}
              type='text'
              size='large'
              status={fieldStatus}
              mask={mask}
              definitions={definitions}
              {...field}
              value={displayValue}
              {...rest}
              className={inputClassName}
              maskOptions={maskOptionsObj}
              onChange={onChange}
              data-qa-selector={qaSelector}
            />
          );
        }}
      </Field>
      <If condition={showErrMsg}>
        <ErrorMessage name={name}>
          {(msg) => <TextError msg={msg} qaSelector={`${qaSelector}-error`} />}
        </ErrorMessage>
      </If>
    </Flex>
  );
});

export default InputMask;
